<template>
  <div>
    <v-dialog
      v-model="isVisible"
      max-width="600"
      persistent
    >
      <CocVerificationSelectionStep
        v-if="isVisible && step === 'verify'"
        @cancel="resetAndCloseDialog"
        @verify="verify"
        :enrollment="enrollment"
        :loading="loading"
        :subsidy-program="subsidyProgram"
        :verification-settings="verificationSettings"
      />

      <CocVerificationConfirmStep
        v-if="isVisible && step === 'confirm'"
        @cancel="resetAndCloseDialog"
        @confirm="confirmVerification"
        :enrollment="enrollment"
        :subsidy-program="subsidyProgram"
        :tags-to-verify="tagsToVerify"
      />

      <CocVerificationRemoveStep
        v-if="isVisible && step === 'remove'"
        @cancel="resetAndCloseDialog"
        @confirm="confirmRemoval"
        :enrollment="enrollment"
        :tags-to-remove="tagsToRemove"
      />

      <CocVerificationPlacementStep
        v-if="isVisible && step === 'placement'"
        @confirm="confirmPlacement"
        :enrollment="enrollment"
      />
    </v-dialog>
  </div>
</template>

<script setup>
import Api from '@/manager/services/bright_finder';
import CocVerificationPlacementStep from '@/manager/components/enrollment/CocVerificationPlacementStep.vue';
import useEventBus from '@/shared/composables/useEventBus';

import CocVerificationConfirmStep from './CocVerificationConfirmStep.vue';
import CocVerificationRemoveStep from './CocVerificationRemoveStep.vue';
import CocVerificationSelectionStep from './CocVerificationSelectionStep.vue';

const eventBus = useEventBus();

defineExpose({ open });

const emit = defineEmits(['change']);

const props = defineProps({
  subsidyProgram: {
    type: Object,
    default: null,
  },
});

const enrollment = ref(null);
const isVisible = ref(false);
const step = ref('verify');
const tagsToVerify = ref([]);
const tagsToRemove = ref([]);
const loading = ref(false);
const verificationSettings = ref({
  current_provider_verified: null,
  sibling_provider_verified: null,
  staff_provider_verified: null,
});
const settingsToSave = ref({});

function setDefaultVerificationSetting(setting) {
  return typeof setting == 'boolean' ? setting : null;
}

function open(enrollmentData) {
  enrollment.value = enrollmentData;

  const { current_provider_verified, sibling_provider_verified, staff_provider_verified } =
    enrollmentData || {};

  verificationSettings.value = {
    current_provider_verified: setDefaultVerificationSetting(current_provider_verified),
    sibling_provider_verified: setDefaultVerificationSetting(sibling_provider_verified),
    staff_provider_verified: setDefaultVerificationSetting(staff_provider_verified),
  };
  step.value = 'verify';
  isVisible.value = true;
}

function resetAndCloseDialog() {
  isVisible.value = false;
  step.value = 'verify';
  enrollment.value = null;
  tagsToVerify.value = [];
  tagsToRemove.value = [];
}

async function verify() {
  const tagSettings = [
    { key: 'current_provider_verified', tag: 'Current student' },
    { key: 'sibling_provider_verified', tag: 'Sibling of student' },
    { key: 'staff_provider_verified', tag: 'Family on staff' },
  ];

  tagSettings.forEach(({ key, tag }) => {
    const computedFlag = key.replace('_verified', '');

    if (verificationSettings.value[key] !== enrollment.value[key]) {
      if (verificationSettings.value[key]) {
        settingsToSave.value = { ...settingsToSave.value, [key]: true };
        tagsToVerify.value = [...tagsToVerify.value, tag];
      } else if (enrollment.value.meta.computed_subsidy_flags[computedFlag]) {
        settingsToSave.value = { ...settingsToSave.value, [key]: false };
        tagsToRemove.value = [...tagsToRemove.value, tag];
      }
    }
  });

  if (props.subsidyProgram.enable_automated_placement_of_coc_enrollments_once_verified) {
    if (tagsToRemove.value.length) {
      step.value = 'remove';
      return;
    }

    const success = await saveEnrollment();
    if (success) {
      await displayPlacementModal();
    }
    return;
  }

  if (tagsToVerify.value.length) {
    step.value = 'confirm';
  } else if (tagsToRemove.value.length) {
    step.value = 'remove';
  }
}

async function confirmVerification() {
  if (tagsToRemove.value.length) {
    step.value = 'remove';
  } else {
    await saveVerification();
  }
}

async function confirmPlacement() {
  resetAndCloseDialog();
  await nextTick();
  emit('change');
}

async function confirmRemoval() {
  if (props.subsidyProgram.enable_automated_placement_of_coc_enrollments_once_verified) {
    const selectedValues = Object.values(verificationSettings.value);

    if (selectedValues.filter((v) => v === false).length !== selectedValues.length) {
      const success = await saveEnrollment();
      if (success) {
        await displayPlacementModal();
      }
      return;
    }
  }

  await saveVerification();
}

async function saveVerification() {
  const success = await saveEnrollment();
  if (success) {
    resetAndCloseDialog();
    await nextTick();
    emit('change');
  }
}

async function saveEnrollment() {
  loading.value = true;
  let success = false;

  if (!enrollment.value.seen_by_provider_at) {
    settingsToSave.value = { ...settingsToSave.value, seen_by_provider: true };
  }

  const response = await Api.manager.enrollment.update(enrollment.value.id, settingsToSave.value);

  if (response?.status === 200) {
    enrollment.value = response.data;
    eventBus.chime('Verification saved');
    success = true;
  } else {
    eventBus.error('Failed to save verification. Please try again.');
  }

  loading.value = false;
  return success;
}

async function displayPlacementModal() {
  if (enrollment.value.status === 'Selected') {
    return confirmPlacement();
  }

  step.value = 'placement';
}
</script>
